import { getVal } from 'react-redux-firebase';
import combineEvents from '@redux-beacon/combine-events';
import GoogleTagManager from '@redux-beacon/google-tag-manager';
import logger from '@redux-beacon/logger';
import dayjs from 'dayjs';
import { createMiddleware } from 'redux-beacon';

import { USER_AUTHENTICATED } from './modules/auth';
import {
  CHECKOUT_ORDER_SUCCESSFUL,
  CHECKOUT_PAYMENT_TYPE_SELECT,
  CHECKOUT_TICKET_SET,
  CHECKOUT_TIMESLOT_SELECT
} from './modules/checkout/types';
import { GAME_SELECTED } from './modules/global/types';

/**
|--------------------------------------------------
| Tracking Event definitions
|--------------------------------------------------
*/

// Checkout steps
export const INVITATION_CHECKOUT_STEP = 1;
export const LOGIN_CHECKOUT_STEP = 2;
export const SELECT_PAYMENT_CHECKOUT_STEP = 3;
export const REVIEW_CHECKOUT_STEP = 4;

const getProduct = (state, quantity) => {
  const timeslot =
    state.firebase.data.checkout && state.firebase.data.checkout.timeslot;
  const product = {
    id: getVal(state.firebase.data, 'checkout.timeslot.time'),
    category: 'Booking',
    brand: state.global.gameId,
    price: getVal(state.firebase.data, 'checkout.timeslot.amount'),
    quantity: quantity || state.checkout.tickets
  };
  if (timeslot) {
    const time = dayjs.unix(timeslot.time);
    product.variant = time.format('ddd');
    product.name = time.format('HH:mm');
  }
  return product;
};

/**
 * Purchase made event
 */
const purchaseMade = (action, prevState) => {
  // If no timeslot ID in redux then 'checkout complete page' revisit so ignore;
  if (!prevState.checkout.timeslotId) {
    return;
  }
  return {
    event: action.type,
    ecommerce: {
      purchase: {
        actionField: {
          id: action.orderId,
          affiliation: 'Booking website',
          revenue: action.order.amount
        },
        products: [getProduct(prevState)]
      }
    },
    transactionId: action.orderId,
    transactionTotal: action.order.amount
  };
};

/**
 * Auth
 */
const authenticated = action => ({
  userId: action.user.uid
});

/**
 * Game Click
 */
const gameClick = action => ({
  gameId: action.gameId
});

/**
 * Timeslot select
 * Equates to adding product to cart
 */
const timeslotSelect = (action, prevState, nextState) => {
  const timeslot = action.timeslot.value;
  const time = dayjs.unix(timeslot.time);
  return {
    event: 'addToCart',
    ecommerce: {
      add: {
        products: [
          {
            id: timeslot.time,
            category: 'Booking',
            name: time.format('HH:mm'),
            variant: time.format('ddd'),
            brand: timeslot.gameId,
            price: timeslot.amount,
            quantity: nextState.checkout.tickets
          }
        ]
      }
    }
  };
};

const timeslotUnselected = (action, prevState) => {
  if (!prevState.checkout.timeslotId) {
    return;
  }
  return {
    event: 'removeFromCart',
    ecommerce: {
      remove: {
        products: [getProduct(prevState)]
      }
    }
  };
};

const ticketsSet = (action, prevState, nextState) => {
  const newTicketLength = action.tickets;
  const oldTicketLength = prevState.checkout.tickets;
  const diff = newTicketLength - oldTicketLength;
  if (!diff) {
    return;
  }
  return {
    event: diff > 0 ? 'addToCart' : 'removeFromCart',
    ecommerce: {
      [diff > 0 ? 'add' : 'remove']: {
        products: [getProduct(nextState, Math.abs(diff))]
      }
    }
  };
};

const paymentTypeSelect = action => {
  return {
    event: 'checkoutOption',
    ecommerce: {
      // eslint-disable-next-line @typescript-eslint/camelcase
      checkout_option: {
        actionField: {
          step: SELECT_PAYMENT_CHECKOUT_STEP,
          option: action.paymentType
        }
      }
    }
  };
};

/**
 * Events mapp
 * Match the event definition to a Redux action
 */
const eventsMap = {
  [USER_AUTHENTICATED]: authenticated,
  [GAME_SELECTED]: gameClick,
  [CHECKOUT_TIMESLOT_SELECT]: combineEvents(timeslotSelect, timeslotUnselected),
  [CHECKOUT_TICKET_SET]: ticketsSet,
  [CHECKOUT_PAYMENT_TYPE_SELECT]: paymentTypeSelect,
  [CHECKOUT_ORDER_SUCCESSFUL]: purchaseMade
};

/**
 * Initialize google analytics and produce redux middleware for logging events
 */
export default function init() {
  /**
   * Redux beacon google tag manager
   */
  const gtm = GoogleTagManager();

  /**
   * Create middleware for GA
   */
  return createMiddleware(eventsMap, gtm, { logger });
}
